import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabase';
import BottomNav from '../BottomNav';
import ReviewCard from './ReviewCard';
import IdentityVerification from './IdentityVerification';
import SideMenu, { IconMenu } from './SideMenu';
import theme from '../theme';

const ADMIN_EMAIL = 'mattif56@gmail.com';

function BadgeVerificatoWhite({ size = 18 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" fill="none" style={{ display: 'inline-block', verticalAlign: 'middle', marginLeft: 5, flexShrink: 0 }}>
      <path d="M8 1L2 3.5V8c0 3.3 2.5 5.8 6 7 3.5-1.2 6-3.7 6-7V3.5L8 1z" fill="rgba(255,255,255,0.95)"/>
      <path d="M5.5 8l2 2 3-3" stroke={theme.colors.primary} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );
}

function InfoRow({ emoji, label, value }) {
  if (!value) return null;
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, fontSize: theme.font.size.md }}>
      <span style={{ fontSize: theme.font.size.md, flexShrink: 0 }}>{emoji}</span>
      <div>
        <div style={{ fontSize: theme.font.size.xs, color: theme.colors.textMuted, marginBottom: 1 }}>{label}</div>
        <div style={{ color: theme.colors.textPrimary }}>{value}</div>
      </div>
    </div>
  );
}

function StatCell({ num, label, sub }) {
  return (
    <div style={{ background: 'rgba(255,255,255,0.13)', borderRadius: theme.radius.md, padding: '8px 6px', textAlign: 'center', flex: 1 }}>
      <div style={{ fontSize: theme.font.size.xxl, fontWeight: 600, color: theme.colors.white, lineHeight: 1 }}>{num}</div>
      {sub && <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.7)', marginTop: 1 }}>{sub}</div>}
      <div style={{ fontSize: 9, color: 'rgba(255,255,255,0.8)', marginTop: 2, lineHeight: 1.3 }}>{label}</div>
    </div>
  );
}

function StarCell({ avg, count, label }) {
  return (
    <div style={{ background: 'rgba(255,255,255,0.13)', borderRadius: theme.radius.md, padding: '8px 6px', textAlign: 'center', flex: 1 }}>
      {avg
        ? <>
            <div style={{ fontSize: theme.font.size.md, color: theme.colors.starGold, lineHeight: 1 }}>★ {avg}</div>
            <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.7)', marginTop: 1 }}>({count} rec.)</div>
          </>
        : <div style={{ fontSize: theme.font.size.sm, color: 'rgba(255,255,255,0.5)', lineHeight: 1.4 }}>—</div>
      }
      <div style={{ fontSize: 9, color: 'rgba(255,255,255,0.8)', marginTop: 2, lineHeight: 1.3 }}>{label}</div>
    </div>
  );
}

function Profile({ session, profile, onProfileUpdate }) {
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [acceptedCountByJob, setAcceptedCountByJob] = useState({});
  const [paidJobIds, setPaidJobIds] = useState(new Set());
  const [applications, setApplications] = useState([]);
  const [onlineJobsCount, setOnlineJobsCount] = useState(0);
  const [completedCommissioned, setCompletedCommissioned] = useState(0);
  const [completedDone, setCompletedDone] = useState(0);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('online');
  const [reviewsAsWorker, setReviewsAsWorker] = useState([]);
  const [reviewsAsOwner, setReviewsAsOwner] = useState([]);
  const [reviewerNames, setReviewerNames] = useState({});
  const [editMode, setEditMode] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [editData, setEditData] = useState({
    full_name: profile?.full_name || '',
    phone: profile?.phone || '',
    bio: profile?.bio || '',
    city: profile?.city || '',
    birth_date: profile?.birth_date || '',
  });
  const [savingProfile, setSavingProfile] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const fileInputRef = useRef(null);
  const personalInfoRef = useRef(null);
  const identityRef = useRef(null);

  useEffect(() => {
    fetchMyJobs();
    fetchMyApplications();
    fetchMyReviews();
  }, []);

  const fetchMyJobs = async () => {
    const myName = profile?.full_name || session?.user?.email;
    const { data, error } = await supabase
      .from('jobs')
      .select('*, applications(id)')
      .eq('author', myName)
      .order('created_at', { ascending: false });
    if (error || !data) { setLoading(false); return; }
    setJobs(data);

    const myJobIds = data.map(j => j.id);
    if (myJobIds.length > 0) {
      const { data: acceptedApps } = await supabase
        .from('applications')
        .select('job_id')
        .in('job_id', myJobIds)
        .eq('status', 'accettata');

      const countMap = {};
      (acceptedApps || []).forEach(app => {
        countMap[app.job_id] = (countMap[app.job_id] || 0) + 1;
      });
      setAcceptedCountByJob(countMap);

      const openCount = data.filter(j => {
        const needed = j.people_needed || 1;
        const accepted = countMap[j.id] || 0;
        return accepted < needed;
      }).length;
      setOnlineJobsCount(openCount);

      const { data: paidApps } = await supabase
        .from('applications')
        .select('job_id')
        .in('job_id', myJobIds)
        .not('amount_paid', 'is', null);

      setCompletedCommissioned((paidApps || []).length);
      const paidSet = new Set((paidApps || []).map(a => a.job_id));
      setPaidJobIds(paidSet);
    }
    setLoading(false);
  };

  const fetchMyApplications = async () => {
    const { data } = await supabase
      .from('applications')
      .select('*, jobs(title, price, category, people_needed)')
      .eq('applicant_email', session?.user?.email)
      .neq('status', 'rifiutata')
      .order('created_at', { ascending: false });

    if (!data) return;

    setCompletedDone(data.filter(a => a.amount_paid != null).length);

    const jobIds = [...new Set(data.map(a => a.job_id))];
    if (jobIds.length === 0) { setApplications([]); return; }

    const { data: otherAccepted } = await supabase
      .from('applications')
      .select('job_id, jobs(people_needed)')
      .in('job_id', jobIds)
      .eq('status', 'accettata')
      .neq('applicant_email', session?.user?.email);

    const otherAcceptedCount = {};
    (otherAccepted || []).forEach(a => {
      otherAcceptedCount[a.job_id] = (otherAcceptedCount[a.job_id] || 0) + 1;
    });

    const activeApplications = data.filter(app => {
      if (app.status === 'accettata' || app.status === 'controproposta' || app.amount_paid != null) {
        return true;
      }
      const needed = app.jobs?.people_needed || 1;
      const othersAccepted = otherAcceptedCount[app.job_id] || 0;
      return othersAccepted < needed;
    });

    setApplications(activeApplications);
  };

  const fetchMyReviews = async () => {
    const name = profile?.full_name;
    if (!name) return;

    const { data } = await supabase
      .from('reviews')
      .select('*')
      .eq('reviewed_email', name)
      .order('created_at', { ascending: false });

    if (!data) return;

    setReviewsAsWorker(data.filter(r => r.role === 'lavoratore'));
    setReviewsAsOwner(data.filter(r => r.role === 'committente'));

    const emails = [...new Set(data.map(r => r.reviewer_email))];
    if (emails.length === 0) return;

    const { data: profiles } = await supabase
      .from('profiles')
      .select('full_name')
      .in('full_name', emails);

    const nameMap = {};
    emails.forEach(e => { nameMap[e] = e; });

    if (profiles && profiles.length > 0) {
      profiles.forEach(p => { nameMap[p.full_name] = p.full_name; });
    }

    const { data: profilesByEmail } = await supabase
      .from('profiles')
      .select('full_name')
      .filter('id', 'in', `(select id from auth.users where email in (${emails.map(e => `'${e}'`).join(',')}))`);

    const nameMapResolved = { ...nameMap };
    for (const email of emails) {
      if (email.includes('@')) {
        const { data: rpcResult } = await supabase.rpc('get_profile_by_email', { user_email: email });
        if (rpcResult && rpcResult.length > 0) {
          nameMapResolved[email] = rpcResult[0].full_name;
        }
      }
    }

    setReviewerNames(nameMapResolved);
  };

  const handleLogout = async () => { await supabase.auth.signOut(); };

  const handleDelete = async (id) => {
    if (!window.confirm('Sei sicuro di voler eliminare questo annuncio?')) return;
    await supabase.from('jobs').delete().eq('id', id);
    setJobs(jobs.filter(j => j.id !== id));
  };

  const handleSaveProfile = async () => {
    if (!editData.full_name.trim()) return;
    setSavingProfile(true);
    await supabase.from('profiles').update({
      full_name: editData.full_name.trim(),
      phone: editData.phone.trim() || null,
      bio: editData.bio.trim() || null,
      city: editData.city.trim() || null,
      birth_date: editData.birth_date || null,
    }).eq('id', session.user.id);
    await onProfileUpdate(session.user.id);
    setSavingProfile(false);
    setEditMode(false);
  };

  const handleAvatarChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { alert('La foto è troppo grande. Massimo 5MB.'); return; }
    setUploadingAvatar(true);
    const ext = file.name.split('.').pop();
    const fileName = `${session.user.id}.${ext}`;
    const { error: uploadError } = await supabase.storage.from('avatars').upload(fileName, file, { upsert: true });
    if (uploadError) { alert('Errore caricamento foto: ' + uploadError.message); setUploadingAvatar(false); return; }
    const { data: urlData } = supabase.storage.from('avatars').getPublicUrl(fileName);
    await supabase.from('profiles').update({ avatar_url: urlData.publicUrl }).eq('id', session.user.id);
    await onProfileUpdate(session.user.id);
    setUploadingAvatar(false);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return null;
    return new Date(dateStr).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const avgStars = (reviews) => {
    if (!reviews.length) return null;
    return (reviews.reduce((sum, r) => sum + r.stars, 0) / reviews.length).toFixed(1);
  };

  const renderStars = (stars) => [1, 2, 3, 4, 5].map(s => (
    <span key={s} style={{ color: s <= stars ? theme.colors.starGold : '#ddd', fontSize: theme.font.size.md }}>★</span>
  ));

  const isJobOnline = (job) => {
    const needed = job.people_needed || 1;
    const accepted = acceptedCountByJob[job.id] || 0;
    return accepted < needed;
  };

  const onlineJobs = jobs.filter(j => isJobOnline(j));
  const completedJobs = jobs.filter(j => paidJobIds.has(j.id));
  const pendingApplications = applications.filter(
    app => app.status !== 'accettata' && app.amount_paid == null
  );

  const displayName = profile?.full_name || session?.user?.email;
  const initials = displayName ? displayName.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) : '?';
  const avatarUrl = profile?.avatar_url;
  const isAdmin = session?.user?.email === ADMIN_EMAIL;
  const identityStatus = profile?.identity_status || 'non_verificato';

  const tabs = [
    { key: 'online', label: '🟢 Online' },
    { key: 'completati', label: '✅ Completati' },
    { key: 'mie candidature', label: 'Mie candidature' },
    { key: 'recensioni', label: 'Recensioni' },
  ];

  const scrollToRef = (ref) => {
    setMenuOpen(false);
    setTimeout(() => {
      ref.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 300);
  };

  const menuItems = [
    {
      emoji: '👤',
      label: 'Modifica informazioni personali',
      onClick: () => {
        setEditData({
          full_name: profile?.full_name || '',
          phone: profile?.phone || '',
          bio: profile?.bio || '',
          city: profile?.city || '',
          birth_date: profile?.birth_date || ''
        });
        setEditMode(true);
        scrollToRef(personalInfoRef);
      },
    },
    {
      emoji: '🛡️',
      label: 'Verifica identità',
      onClick: () => scrollToRef(identityRef),
    },
    {
      emoji: '💳',
      label: 'Pagamenti',
      onClick: () => navigate('/pagamenti'),
    },
    {
      emoji: '🚫',
      label: 'Politica di cancellazione',
      onClick: () => navigate('/politica-cancellazione'),
    },
    {
      emoji: '📖',
      label: 'Come funziona',
      onClick: () => navigate('/guide'),
    },
    ...(isAdmin ? [{
      emoji: '🛡️',
      label: 'Pannello Admin',
      onClick: () => navigate('/admin'),
    }] : []),
    {
      emoji: '❤️',
      label: 'Annunci preferiti',
      onClick: () => navigate('/preferiti'),
    },
    {
      emoji: '🚩',
      label: 'Segnala un problema',
      onClick: () => navigate('/segnala-problema'),
    },
  ];

  return (
    <div style={{ maxWidth: theme.layout.maxWidth, margin: '0 auto', fontFamily: theme.font.family }}>

      <div style={{ background: theme.colors.primary, padding: '20px 20px 18px', color: theme.colors.white, textAlign: 'center' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: theme.spacing.md }}>
          <div style={{ width: 36 }} />
          <div style={{ position: 'relative', display: 'inline-block' }}>
            <div onClick={() => fileInputRef.current?.click()} style={{
              width: 72, height: 72, borderRadius: theme.radius.circle,
              background: 'rgba(255,255,255,0.25)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontWeight: 700, fontSize: theme.font.size.xxl, color: theme.colors.white, cursor: 'pointer',
              overflow: 'hidden', border: '2px solid rgba(255,255,255,0.5)'
            }}>
              {avatarUrl
                ? <img src={avatarUrl} alt="avatar" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                : initials}
            </div>
            <div onClick={() => fileInputRef.current?.click()} style={{
              position: 'absolute', bottom: 0, right: 0, background: theme.colors.white,
              borderRadius: theme.radius.circle, width: 22, height: 22, display: 'flex',
              alignItems: 'center', justifyContent: 'center', fontSize: theme.font.size.sm,
              cursor: 'pointer', boxShadow: '0 1px 4px rgba(0,0,0,0.2)'
            }}>✏️</div>
            <input ref={fileInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleAvatarChange} />
          </div>
          <button onClick={() => setMenuOpen(true)} style={{
            background: 'rgba(255,255,255,0.2)', border: 'none', borderRadius: theme.radius.circle,
            width: 36, height: 36, display: 'flex', alignItems: 'center', justifyContent: 'center',
            cursor: 'pointer', marginTop: 4,
          }}>
            <IconMenu />
          </button>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 2 }}>
          <span style={{ fontSize: theme.font.size.xl, fontWeight: 700 }}>{displayName}</span>
          {identityStatus === 'verificato' && <BadgeVerificatoWhite size={18} />}
        </div>
        <div style={{ fontSize: theme.font.size.base, opacity: 0.8, marginBottom: 4 }}>{session?.user?.email}</div>
        {profile?.city && <div style={{ fontSize: theme.font.size.sm, opacity: 0.75, marginBottom: 6 }}>📍 {profile.city}</div>}
        {uploadingAvatar && <div style={{ fontSize: theme.font.size.sm, opacity: 0.8, marginBottom: 6 }}>Caricamento foto...</div>}

        <div style={{ borderTop: '0.5px solid rgba(255,255,255,0.2)', margin: '10px 0' }} />

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, marginBottom: 6 }}>
          <StatCell num={completedDone} label="Lavori svolti" sub="💰 pagati" />
          <StarCell avg={avgStars(reviewsAsWorker)} count={reviewsAsWorker.length} label="⭐ come lavoratore" />
          <StatCell num={completedCommissioned} label="Lavori commissionati" sub="💰 pagati" />
          <StarCell avg={avgStars(reviewsAsOwner)} count={reviewsAsOwner.length} label="⭐ come committente" />
        </div>
        <div style={{ background: 'rgba(255,255,255,0.13)', borderRadius: theme.radius.md, padding: '8px 6px', textAlign: 'center' }}>
          <div style={{ fontSize: theme.font.size.xxl, fontWeight: 600, color: theme.colors.white, lineHeight: 1 }}>{onlineJobsCount}</div>
          <div style={{ fontSize: 9, color: 'rgba(255,255,255,0.8)', marginTop: 2 }}>📋 Annunci online (candidature aperte)</div>
        </div>
      </div>

      <div style={{ padding: theme.spacing.xl, paddingBottom: 80 }}>

        <div ref={personalInfoRef} style={{ border: `1px solid ${theme.colors.border}`, borderRadius: theme.radius.lg, padding: theme.spacing.lg, marginBottom: theme.spacing.lg }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
            <span style={{ fontWeight: 600, fontSize: theme.font.size.lg }}>👤 Informazioni personali</span>
            <button onClick={() => {
              setEditData({
                full_name: profile?.full_name || '',
                phone: profile?.phone || '',
                bio: profile?.bio || '',
                city: profile?.city || '',
                birth_date: profile?.birth_date || ''
              });
              setEditMode(!editMode);
            }} style={{ background: 'none', border: `1px solid ${theme.colors.primary}`, color: theme.colors.primary, borderRadius: theme.radius.sm, padding: '4px 12px', fontSize: theme.font.size.base, cursor: 'pointer' }}>
              {editMode ? 'Annulla' : '✏️ Modifica'}
            </button>
          </div>
          {editMode ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing.md }}>
              <div>
                <label style={{ fontSize: theme.font.size.sm, color: theme.colors.textMuted, display: 'block', marginBottom: 4 }}>Nome completo</label>
                <input value={editData.full_name} onChange={e => setEditData({ ...editData, full_name: e.target.value })}
                  style={{ width: '100%', padding: '8px 12px', borderRadius: theme.radius.sm, border: `1px solid ${theme.colors.borderInput}`, fontSize: theme.font.size.md, boxSizing: 'border-box' }} />
              </div>
              <div>
                <label style={{ fontSize: theme.font.size.sm, color: theme.colors.textMuted, display: 'block', marginBottom: 4 }}>Data di nascita</label>
                <input type="date" value={editData.birth_date} onChange={e => setEditData({ ...editData, birth_date: e.target.value })}
                  style={{ width: '100%', padding: '8px 12px', borderRadius: theme.radius.sm, border: `1px solid ${theme.colors.borderInput}`, fontSize: theme.font.size.md, boxSizing: 'border-box' }} />
              </div>
              <div>
                <label style={{ fontSize: theme.font.size.sm, color: theme.colors.textMuted, display: 'block', marginBottom: 4 }}>Numero di telefono</label>
                <input type="tel" value={editData.phone} onChange={e => setEditData({ ...editData, phone: e.target.value })}
                  placeholder="Es. +39 333 1234567"
                  style={{ width: '100%', padding: '8px 12px', borderRadius: theme.radius.sm, border: `1px solid ${theme.colors.borderInput}`, fontSize: theme.font.size.md, boxSizing: 'border-box' }} />
              </div>
              <div>
                <label style={{ fontSize: theme.font.size.sm, color: theme.colors.textMuted, display: 'block', marginBottom: 4 }}>Città di residenza</label>
                <input value={editData.city} onChange={e => setEditData({ ...editData, city: e.target.value })}
                  placeholder="Es. Parma"
                  style={{ width: '100%', padding: '8px 12px', borderRadius: theme.radius.sm, border: `1px solid ${theme.colors.borderInput}`, fontSize: theme.font.size.md, boxSizing: 'border-box' }} />
              </div>
              <div>
                <label style={{ fontSize: theme.font.size.sm, color: theme.colors.textMuted, display: 'block', marginBottom: 4 }}>Bio</label>
                <textarea value={editData.bio} onChange={e => setEditData({ ...editData, bio: e.target.value })}
                  placeholder="Presentati in poche righe..." rows={3}
                  style={{ width: '100%', padding: '8px 12px', borderRadius: theme.radius.sm, border: `1px solid ${theme.colors.borderInput}`, fontSize: theme.font.size.md, boxSizing: 'border-box', resize: 'none' }} />
              </div>
              <button onClick={handleSaveProfile} disabled={savingProfile} style={{
                width: '100%', background: savingProfile ? theme.colors.textMuted : theme.colors.primary, color: theme.colors.white,
                border: 'none', borderRadius: theme.radius.md, padding: 12, fontSize: theme.font.size.lg,
                cursor: savingProfile ? 'not-allowed' : 'pointer', fontWeight: 600
              }}>{savingProfile ? 'Salvataggio...' : '✓ Salva modifiche'}</button>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing.sm }}>
              <InfoRow emoji="👤" label="Nome" value={profile?.full_name} />
              <InfoRow emoji="📅" label="Data di nascita" value={formatDate(profile?.birth_date)} />
              <InfoRow emoji="📞" label="Telefono" value={profile?.phone} />
              <InfoRow emoji="📍" label="Città" value={profile?.city} />
              <InfoRow emoji="📝" label="Bio" value={profile?.bio} />
            </div>
          )}
        </div>

        <div ref={identityRef} style={{ border: `1px solid ${theme.colors.border}`, borderRadius: theme.radius.lg, padding: theme.spacing.lg, marginBottom: theme.spacing.xl }}>
          <div style={{ fontWeight: 600, fontSize: theme.font.size.lg, marginBottom: 14 }}>🛡️ Verifica identità</div>
          <IdentityVerification session={session} profile={profile} onProfileUpdate={onProfileUpdate} />
        </div>

        <div style={{ display: 'flex', borderBottom: '1px solid #eee', marginBottom: theme.spacing.lg, overflowX: 'auto' }}>
          {tabs.map(tab => (
            <button key={tab.key} onClick={() => setActiveTab(tab.key)} style={{
              flexShrink: 0, padding: '10px 12px', border: 'none', background: 'none',
              borderBottom: activeTab === tab.key ? `2px solid ${theme.colors.primary}` : '2px solid transparent',
              color: activeTab === tab.key ? theme.colors.primary : theme.colors.textMuted,
              cursor: 'pointer', fontSize: theme.font.size.sm, fontWeight: activeTab === tab.key ? 600 : 400,
              whiteSpace: 'nowrap',
            }}>{tab.label}</button>
          ))}
        </div>

        {activeTab === 'online' && (
          <>
            {loading && <p style={{ color: theme.colors.textMuted, textAlign: 'center' }}>Caricamento...</p>}
            {!loading && onlineJobs.length === 0 && (
              <p style={{ color: theme.colors.textMuted, textAlign: 'center', marginTop: 30 }}>Nessun annuncio online al momento.</p>
            )}
            {onlineJobs.map(job => (
              <div key={job.id} style={{ border: `1px solid ${theme.colors.border}`, borderRadius: theme.radius.lg, padding: theme.spacing.lg, marginBottom: theme.spacing.md }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                  <span style={{ fontWeight: 600, fontSize: theme.font.size.lg }}>{job.title}</span>
                  <span style={{ color: theme.colors.primary, fontWeight: 600 }}>{job.price}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <span style={{ background: theme.colors.primaryLight, color: theme.colors.primary, padding: '2px 8px', borderRadius: theme.radius.md, fontSize: theme.font.size.sm }}>{job.category}</span>
                    {job.applications?.length > 0 && (
                      <span style={{ background: theme.colors.warningBg, color: theme.colors.warning, padding: '2px 8px', borderRadius: theme.radius.md, fontSize: theme.font.size.sm }}>
                        {job.applications.length} candidature
                      </span>
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: 10 }}>
                    <button onClick={() => navigate(`/modifica-annuncio/${job.id}`)} style={{ background: 'none', border: 'none', color: theme.colors.primary, fontSize: theme.font.size.base, cursor: 'pointer' }}>✏️ Modifica</button>
                    <button onClick={() => handleDelete(job.id)} style={{ background: 'none', border: 'none', color: theme.colors.error, fontSize: theme.font.size.base, cursor: 'pointer' }}>🗑 Elimina</button>
                  </div>
                </div>
              </div>
            ))}
          </>
        )}

        {activeTab === 'completati' && (
          <>
            {loading && <p style={{ color: theme.colors.textMuted, textAlign: 'center' }}>Caricamento...</p>}
            {!loading && completedJobs.length === 0 && (
              <p style={{ color: theme.colors.textMuted, textAlign: 'center', marginTop: 30 }}>Nessun lavoro pagato ancora.</p>
            )}
            {completedJobs.map(job => (
              <div key={job.id} style={{ border: `1px solid ${theme.colors.border}`, borderRadius: theme.radius.lg, padding: theme.spacing.lg, marginBottom: theme.spacing.md, opacity: 0.75 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                  <span style={{ fontWeight: 600, fontSize: theme.font.size.lg }}>{job.title}</span>
                  <span style={{ color: theme.colors.textMuted, fontWeight: 600 }}>{job.price}</span>
                </div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <span style={{ background: theme.colors.backgroundAlt, color: theme.colors.textMuted, padding: '2px 8px', borderRadius: theme.radius.md, fontSize: theme.font.size.sm }}>{job.category}</span>
                  <span style={{ background: theme.colors.primaryLight, color: theme.colors.primary, padding: '2px 8px', borderRadius: theme.radius.md, fontSize: theme.font.size.sm }}>💰 Pagato</span>
                  {job.city && <span style={{ fontSize: theme.font.size.sm, color: '#bbb' }}>📍 {job.city}</span>}
                </div>
              </div>
            ))}
          </>
        )}

        {activeTab === 'mie candidature' && (
          <>
            {pendingApplications.length === 0 && (
              <p style={{ color: theme.colors.textMuted, textAlign: 'center', marginTop: 30 }}>Nessuna candidatura in attesa al momento.</p>
            )}
            {pendingApplications.map(app => (
              <div key={app.id} style={{ border: `1px solid ${theme.colors.border}`, borderRadius: theme.radius.lg, padding: theme.spacing.lg, marginBottom: theme.spacing.md }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                  <span style={{ fontWeight: 600, fontSize: theme.font.size.lg }}>{app.jobs?.title}</span>
                  <span style={{ color: theme.colors.primary, fontWeight: 600 }}>{app.jobs?.price}</span>
                </div>
                {app.proposed_price && (
                  <div style={{ fontSize: theme.font.size.base, color: theme.colors.primary, marginBottom: 4, fontWeight: 600 }}>
                    💰 Prezzo proposto: €{app.proposed_price}
                  </div>
                )}
                {app.selected_dates && app.selected_dates.length > 0 && (
                  <div style={{ fontSize: theme.font.size.sm, color: theme.colors.textSecondary, marginBottom: 4 }}>
                    📅 Date: {app.selected_dates.map(d => new Date(d + 'T00:00:00').toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit' })).join(', ')}
                  </div>
                )}
                {app.message && <div style={{ fontSize: theme.font.size.base, color: theme.colors.textSecondary, marginBottom: theme.spacing.sm }}>{app.message}</div>}
                <span style={{
                  background: app.status === 'controproposta' ? theme.colors.infoBg : theme.colors.backgroundAlt,
                  color: app.status === 'controproposta' ? theme.colors.info : theme.colors.textSecondary,
                  padding: '3px 10px', borderRadius: theme.radius.md, fontSize: theme.font.size.sm
                }}>
                  {app.status === 'controproposta' ? '💬 In negoziazione' : '⏳ In attesa'}
                </span>
              </div>
            ))}
          </>
        )}

        {activeTab === 'recensioni' && (
          <div>
            <div style={{ marginBottom: 28 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                <span style={{ fontSize: theme.font.size.lg }}>👷</span>
                <span style={{ fontWeight: 700, fontSize: theme.font.size.lg, color: theme.colors.textPrimary }}>Come lavoratore</span>
                {avgStars(reviewsAsWorker) && (
                  <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <span style={{ color: theme.colors.starGold, fontSize: theme.font.size.lg }}>★</span>
                    <span style={{ fontWeight: 700, fontSize: theme.font.size.base }}>{avgStars(reviewsAsWorker)}</span>
                    <span style={{ color: theme.colors.textMuted, fontSize: theme.font.size.base }}>({reviewsAsWorker.length})</span>
                  </span>
                )}
              </div>
              {reviewsAsWorker.length === 0
                ? <p style={{ color: theme.colors.textMuted, fontSize: theme.font.size.md, textAlign: 'center', padding: '20px 0' }}>Nessuna recensione ricevuta come lavoratore ancora.</p>
                : reviewsAsWorker.map(r => (
                    <ReviewCard
                      key={r.id}
                      review={r}
                      reviewerName={reviewerNames[r.reviewer_email] || r.reviewer_email}
                      renderStars={renderStars}
                    />
                  ))
              }
            </div>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                <span style={{ fontSize: theme.font.size.lg }}>💼</span>
                <span style={{ fontWeight: 700, fontSize: theme.font.size.lg, color: theme.colors.textPrimary }}>Come committente</span>
                {avgStars(reviewsAsOwner) && (
                  <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <span style={{ color: theme.colors.starGold, fontSize: theme.font.size.lg }}>★</span>
                    <span style={{ fontWeight: 700, fontSize: theme.font.size.base }}>{avgStars(reviewsAsOwner)}</span>
                    <span style={{ color: theme.colors.textMuted, fontSize: theme.font.size.base }}>({reviewsAsOwner.length})</span>
                  </span>
                )}
              </div>
              {reviewsAsOwner.length === 0
                ? <p style={{ color: theme.colors.textMuted, fontSize: theme.font.size.md, textAlign: 'center', padding: '20px 0' }}>Nessuna recensione ricevuta come committente ancora.</p>
                : reviewsAsOwner.map(r => (
                    <ReviewCard
                      key={r.id}
                      review={r}
                      reviewerName={reviewerNames[r.reviewer_email] || r.reviewer_email}
                      renderStars={renderStars}
                    />
                  ))
              }
            </div>
          </div>
        )}
      </div>

      <SideMenu open={menuOpen} onClose={() => setMenuOpen(false)} items={menuItems} onLogout={handleLogout} />

      <BottomNav />
    </div>
  );
}

export default Profile;